import React, { useMemo } from 'react'
import { buildHourlyWorkload, DAY_START_HOUR, DAY_END_HOUR } from '../allocationUtils'

/**
 * WorkloadProfileFooter
 * ---------------------
 * Displays the aggregated hourly workload profile for all active resources across the day.
 * Renders an hourly bar visualization that aligns with the Gantt columns.
 */
export default function WorkloadProfileFooter({
  employees = [],
  dateStr,
  hours = [],
  slotWidth = 120,
}) {
  // Aggregate workload hours for each hour slot across all visible employees
  const { hourlyTotals, maxWorkload, totalDayHours } = useMemo(() => {
    const totals = new Array(hours.length).fill(0)

    employees.forEach((emp) => {
      const slots = buildHourlyWorkload(emp.job_blocks || [], dateStr, DAY_START_HOUR, DAY_END_HOUR)
      slots.forEach((val, idx) => {
        if (idx < totals.length) {
          totals[idx] += val
        }
      })
    })

    const max = Math.max(...totals, 4)
    const sum = totals.reduce((acc, v) => acc + v, 0)
    return { hourlyTotals: totals, maxWorkload: max, totalDayHours: sum }
  }, [employees, dateStr, hours])

  const totalWidth = hours.length * slotWidth

  return (
    <div
      className="flex border-t-2 border-outline-variant/40 bg-surface-container-lowest/90 backdrop-blur-xs select-none"
      style={{
        width: `${totalWidth}px`,
        minWidth: `${totalWidth}px`,
      }}
    >
      {hours.map(({ hour, label }, i) => {
        const val = hourlyTotals[i] || 0
        const heightPct = Math.min(100, Math.round((val / maxWorkload) * 100))
        // Color intensity based on load
        const barColor =
          val > 15
            ? 'bg-red-500'
            : val > 8
            ? 'bg-amber-500'
            : val > 0
            ? 'bg-primary'
            : 'bg-outline-variant/20'

        return (
          <div
            key={hour}
            className="flex-shrink-0 flex flex-col justify-end items-center px-1.5 pt-2 pb-1.5 border-r border-outline-variant/20 h-20 transition-colors hover:bg-surface-variant/20 group/slot"
            style={{ width: `${slotWidth}px` }}
            title={`${label}: ${val.toFixed(1)} committed hours across ${employees.length} employees`}
          >
            {/* Value badge */}
            <span
              className={`text-[10px] font-semibold mb-1 transition-opacity ${
                val > 0 ? 'text-on-surface opacity-90' : 'text-outline/40 opacity-0 group-hover/slot:opacity-100'
              }`}
            >
              {val > 0 ? `${val.toFixed(1)}h` : '0h'}
            </span>

            {/* Bar container */}
            <div className="w-full bg-surface-variant/30 rounded-t-sm h-8 flex items-end overflow-hidden p-0.5">
              <div
                className={`w-full rounded-xs transition-all duration-300 ${barColor}`}
                style={{ height: `${Math.max(4, heightPct)}%` }}
              />
            </div>

            {/* Hour label */}
            <span className="text-[10px] text-outline font-medium mt-1 truncate">
              {label}
            </span>
          </div>
        )
      })}
    </div>
  )
}
